import type { AnyAction, Dispatch } from '@reduxjs/toolkit';
import type { AppThunk, RootState } from '../../../store';
import { isValidRegion } from '../../../config/region';
import { fetchSessionIndicators } from '../../../apis/totalUnreadApi';
import { persistTenantSessions, selectAllTenantSessions, setSessionIndicators } from '.';

/**
 * Fetches all session indicators (alarm, unread, muster) for one tenant and
 * dispatches `setSessionIndicators` only if something changed. Returns whether
 * a change was applied so callers can decide when to persist.
 */
async function reconcileSessionIndicators(
  tenantId: number,
  dispatch: Dispatch<AnyAction>,
  getState: () => RootState
): Promise<boolean> {
  const session = getState().organizations.sessions.find(s => s.tenantId === tenantId);
  if (!session?.accessToken) return false;
  if (!isValidRegion(session.region)) return false;

  try {
    const indicators = await fetchSessionIndicators(session.region, session.accessToken);
    const unchanged =
      Boolean(session.hasActiveAlarm) === indicators.hasActiveAlarm &&
      Boolean(session.hasUnreadMessages) === indicators.hasUnreadMessages &&
      Boolean(session.hasActiveImOk) === indicators.hasActiveImOk;
    if (unchanged) return false;
    dispatch(setSessionIndicators({ tenantId, ...indicators }));
    return true;
  } catch (error) {
    console.error('[OrganizationsSlice] reconcileSessionIndicators failed', { tenantId, error });
    return false;
  }
}

/**
 * Re-read `ActiveEmergency` from the server for a single tenant session.
 *
 * Used when an inactive-session push arrives — particularly for `recalled`
 * events, where multiple alarms can be active concurrently in a tenant, so a
 * single recall must not unconditionally clear the indicator.
 */
export const refreshSessionAlarm =
  (tenantId: number): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const changed = await reconcileSessionIndicators(tenantId, dispatch, getState);
    if (changed) persistTenantSessions(getState());
  };

/**
 * Refresh the alarm flag for every session that is not the currently active
 * one. Called on org switch and on app foreground — push notifications remain
 * the primary source of truth, this just reconciles drift.
 *
 * Persists once at the end if any flag actually changed, instead of N times.
 */
export const syncInactiveSessionAlarms =
  (): AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    const activeId = state.organizations.activeTenantId;
    const sessions = selectAllTenantSessions(state);

    const results = await Promise.all(
      sessions
        .filter(s => s.tenantId !== activeId)
        .map(s => reconcileSessionIndicators(s.tenantId, dispatch, getState))
    );

    if (results.some(Boolean)) persistTenantSessions(getState());
  };
